import React, { useEffect, useMemo, useState } from "react";
import api from "../lib/api";
import { toast } from "sonner";
import { Plus, Trash2, KeyRound, Save, X, Shield, Calendar, GraduationCap, Link2 } from "lucide-react";

const fmtDate = (d) => d ? new Date(d).toLocaleDateString("fr-FR") : "—";

const AdminUsersAdmin = () => {
  const [items, setItems] = useState([]);
  const [teachers, setTeachers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [draft, setDraft] = useState({ email: "", password: "", name: "", teacherId: "" });
  const [pwdEditing, setPwdEditing] = useState(null);
  const [pwdDraft, setPwdDraft] = useState("");
  const [teacherEditing, setTeacherEditing] = useState(null);
  const [teacherDraft, setTeacherDraft] = useState("");
  const currentEmail = localStorage.getItem("elan_admin_email");

  const load = async () => {
    setLoading(true);
    try {
      const [a, t] = await Promise.all([
        api.get("/admin/admins"),
        api.get("/admin/teachers"),
      ]);
      setItems(a.data || []);
      setTeachers(t.data || []);
    } catch { toast.error("Erreur de chargement"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const linkedTeacherIds = useMemo(() => new Set(items.map((u) => u.teacherId).filter(Boolean)), [items]);
  const availableTeachersForDraft = useMemo(
    () => teachers.filter((t) => !linkedTeacherIds.has(t.id)),
    [teachers, linkedTeacherIds]
  );

  const create = async () => {
    if (!draft.email || !draft.password) return toast.error("Email et mot de passe requis");
    if (draft.password.length < 6) return toast.error("Mot de passe trop court (6+ caractères)");
    try {
      await api.post("/admin/admins", draft);
      toast.success("Administrateur ajouté");
      setShowCreate(false);
      setDraft({ email: "", password: "", name: "", teacherId: "" });
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Erreur"); }
  };

  const savePassword = async (uid) => {
    if (!pwdDraft || pwdDraft.length < 6) return toast.error("Mot de passe trop court (6+ caractères)");
    try {
      await api.put(`/admin/admins/${uid}/password`, { password: pwdDraft });
      toast.success("Mot de passe mis à jour");
      setPwdEditing(null); setPwdDraft("");
    } catch (e) { toast.error(e?.response?.data?.detail || "Erreur"); }
  };

  const saveTeacherLink = async (uid) => {
    try {
      await api.patch(`/admin/admins/${uid}`, { teacherId: teacherDraft });
      toast.success(teacherDraft ? "Compte lié au professeur" : "Lien supprimé");
      setTeacherEditing(null); setTeacherDraft("");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Erreur"); }
  };

  const del = async (u) => {
    if (!window.confirm(`Supprimer l'administrateur ${u.email} ?`)) return;
    try {
      await api.delete(`/admin/admins/${u.id}`);
      toast.success("Administrateur supprimé");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Erreur"); }
  };

  return (
    <div data-testid="admin-users">
      <div className="flex items-start justify-between gap-3 mb-4 flex-wrap">
        <div>
          <h1 className="font-serif-display text-2xl sm:text-3xl text-stone-800">Administrateurs</h1>
          <p className="text-stone-500 mt-1 text-sm sm:text-base">Comptes ayant accès au back-office. Vous pouvez lier un compte admin à une fiche professeur pour préremplir certaines informations.</p>
        </div>
        {!showCreate && (
          <button onClick={() => setShowCreate(true)} data-testid="admin-add-button" className="inline-flex items-center gap-2 bg-coral text-white px-4 py-2 rounded-full text-sm font-medium shrink-0">
            <Plus size={16}/> Ajouter un admin
          </button>
        )}
      </div>

      {showCreate && (
        <div className="bg-white rounded-2xl shadow-sm p-5 sm:p-6 my-5 border-2 border-blush-light">
          <h3 className="font-serif-display text-xl text-stone-800 mb-4">Nouvel administrateur</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Field label="Nom (optionnel)" value={draft.name} onChange={(v) => setDraft({...draft, name: v})} testId="new-admin-name" />
            <Field label="Email *" type="email" autoComplete="off" value={draft.email} onChange={(v) => setDraft({...draft, email: v})} testId="new-admin-email" />
            <Field label="Mot de passe *" type="password" autoComplete="new-password" value={draft.password} onChange={(v) => setDraft({...draft, password: v})} testId="new-admin-password" />
            <div>
              <label className="block text-xs uppercase tracking-widest text-stone-500 mb-1.5">Lier à un professeur (optionnel)</label>
              <select
                value={draft.teacherId}
                onChange={(e) => setDraft({...draft, teacherId: e.target.value})}
                data-testid="new-admin-teacher-link"
                className="w-full bg-stone-50 border border-stone-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blush"
              >
                <option value="">— Aucun (admin uniquement) —</option>
                {availableTeachersForDraft.map((t) => <option key={t.id} value={t.id}>{t.name}{t.discipline ? ` — ${t.discipline}` : ""}</option>)}
              </select>
              <p className="text-stone-500 text-xs mt-1">Si lié : ce compte aura aussi accès à l&apos;espace prof.</p>
            </div>
          </div>
          <div className="flex flex-wrap gap-3 mt-5">
            <button onClick={create} data-testid="new-admin-save" className="inline-flex items-center gap-2 bg-coral text-white px-5 py-2 rounded-full text-sm"><Save size={16}/> Créer</button>
            <button onClick={() => { setShowCreate(false); setDraft({ email: "", password: "", name: "", teacherId: "" }); }} className="inline-flex items-center gap-2 bg-stone-100 text-stone-700 px-5 py-2 rounded-full text-sm"><X size={16}/> Annuler</button>
          </div>
        </div>
      )}

      {loading ? (
        <div className="text-stone-500 p-8 text-center">Chargement...</div>
      ) : (
        <div className="space-y-3">
          {items.map((u) => (
            <div key={u.id} className="bg-white rounded-2xl shadow-sm p-4 sm:p-5" data-testid={`admin-row-${u.id}`}>
              <div className="flex items-start gap-3 flex-wrap">
                <div className="w-10 h-10 rounded-full bg-blush-light flex items-center justify-center text-deep-rose shrink-0"><Shield size={18}/></div>
                <div className="min-w-0 flex-1">
                  <div className="font-serif-display text-stone-800 truncate">
                    {u.email}
                    {u.email === currentEmail && <span className="ml-2 text-[10px] uppercase tracking-widest bg-emerald-100 text-emerald-700 px-2 py-0.5 rounded-full">Vous</span>}
                  </div>
                  {u.name && <div className="text-stone-500 text-sm">{u.name}</div>}
                  <div className="text-stone-400 text-xs mt-1 flex flex-wrap gap-x-2 gap-y-0.5">
                    <span className="inline-flex items-center gap-1"><Calendar size={12}/> Créé le {fmtDate(u.createdAt)}</span>
                    {u.teacher && (
                      <span className="inline-flex items-center gap-1 text-deep-rose">
                        <GraduationCap size={12}/> Prof : {u.teacher.name}{u.teacher.discipline ? ` (${u.teacher.discipline})` : ""}
                      </span>
                    )}
                  </div>
                </div>
                <div className="flex gap-2 shrink-0 flex-wrap">
                  <button onClick={() => { setTeacherEditing(u.id); setTeacherDraft(u.teacherId || ""); }} data-testid={`admin-teacher-edit-${u.id}`} className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs ${u.teacher ? "bg-emerald-50 hover:bg-emerald-100 text-emerald-700" : "bg-blush-light/60 hover:bg-blush-light text-deep-rose"}`}>
                    {u.teacher ? <><GraduationCap size={12}/> Lié à un prof — modifier</> : <><Link2 size={12}/> Lier à un prof</>}
                  </button>
                  <button onClick={() => { setPwdEditing(u.id); setPwdDraft(""); }} data-testid={`admin-pw-${u.id}`} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-stone-100 hover:bg-stone-200 text-stone-700 text-xs">
                    <KeyRound size={12}/> Mot de passe
                  </button>
                  {u.email !== currentEmail && (
                    <button onClick={() => del(u)} data-testid={`admin-delete-${u.id}`} className="p-2 rounded-lg bg-red-50 hover:bg-red-100 text-red-600"><Trash2 size={16}/></button>
                  )}
                </div>
              </div>

              {pwdEditing === u.id && (
                <div className="mt-3 bg-stone-50 border border-stone-200 rounded-xl p-3 flex flex-col sm:flex-row gap-2">
                  <input type="password" placeholder="Nouveau mot de passe (6+ caractères)" value={pwdDraft} onChange={(e) => setPwdDraft(e.target.value)} data-testid={`admin-pw-input-${u.id}`} className="flex-1 bg-white border border-stone-200 rounded px-3 py-2 text-sm" />
                  <button onClick={() => savePassword(u.id)} data-testid={`admin-pw-save-${u.id}`} className="inline-flex items-center justify-center gap-1.5 bg-coral text-white rounded-full px-4 py-2 text-xs"><Save size={12}/> Enregistrer</button>
                  <button onClick={() => { setPwdEditing(null); setPwdDraft(""); }} className="inline-flex items-center justify-center gap-1.5 bg-stone-200 text-stone-700 rounded-full px-4 py-2 text-xs"><X size={12}/> Annuler</button>
                </div>
              )}

              {teacherEditing === u.id && (
                <div className="mt-3 bg-blush-light/30 border border-blush-light rounded-xl p-3 flex flex-col sm:flex-row gap-2">
                  <select
                    value={teacherDraft}
                    onChange={(e) => setTeacherDraft(e.target.value)}
                    data-testid={`admin-teacher-select-${u.id}`}
                    className="flex-1 bg-white border border-stone-200 rounded px-3 py-2 text-sm"
                  >
                    <option value="">— Aucun (retirer le lien) —</option>
                    {teachers.filter((t) => !linkedTeacherIds.has(t.id) || t.id === u.teacherId).map((t) => (
                      <option key={t.id} value={t.id}>{t.name}{t.discipline ? ` — ${t.discipline}` : ""}</option>
                    ))}
                  </select>
                  <button onClick={() => saveTeacherLink(u.id)} data-testid={`admin-teacher-save-${u.id}`} className="inline-flex items-center justify-center gap-1.5 bg-coral text-white rounded-full px-4 py-2 text-xs"><Save size={12}/> Enregistrer</button>
                  <button onClick={() => { setTeacherEditing(null); setTeacherDraft(""); }} className="inline-flex items-center justify-center gap-1.5 bg-stone-200 text-stone-700 rounded-full px-4 py-2 text-xs"><X size={12}/> Annuler</button>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

const Field = ({ label, value, onChange, type = "text", autoComplete, testId }) => (
  <div>
    <label className="block text-xs uppercase tracking-widest text-stone-500 mb-1.5">{label}</label>
    <input
      type={type}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      autoComplete={autoComplete}
      data-testid={testId}
      className="w-full bg-stone-50 border border-stone-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blush"
    />
  </div>
);

export default AdminUsersAdmin;
